import { Link } from "react-router-dom";
import photoTransferImage from "@/assets/editorial/android-switch/photo-transfer.webp";
import lineTransferImage from "@/assets/editorial/android-switch/line-transfer.webp";
import suicaAuthImage from "@/assets/editorial/android-switch/suica-auth.webp";
import switchingChecklistImage from "@/assets/editorial/android-switch/switching-checklist.webp";
import dataUsageHistoryImage from "@/assets/editorial/article-illustrations/data-usage/detail-1.webp";
import dataUsageCapacityImage from "@/assets/editorial/article-illustrations/data-usage/detail-2.webp";
import dataUsageSceneImage from "@/assets/editorial/article-illustrations/data-usage/detail-3.webp";
import iphoneBuyMethodImage from "@/assets/editorial/article-illustrations/iphone-buy/detail-1.webp";
import iphoneBuyTotalImage from "@/assets/editorial/article-illustrations/iphone-buy/detail-2.webp";
import iphoneBuyUsedImage from "@/assets/editorial/article-illustrations/iphone-buy/detail-3.webp";
import repairBatteryImage from "@/assets/editorial/article-illustrations/iphone-repair-or-replace/detail-1.webp";
import repairCostImage from "@/assets/editorial/article-illustrations/iphone-repair-or-replace/detail-2.webp";
import repairBackupImage from "@/assets/editorial/article-illustrations/iphone-repair-or-replace/detail-3.webp";
import usedIphoneLockImage from "@/assets/editorial/article-illustrations/used-iphone-checklist/detail-1.webp";
import usedIphoneConditionImage from "@/assets/editorial/article-illustrations/used-iphone-checklist/detail-2.webp";
import usedIphoneTestImage from "@/assets/editorial/article-illustrations/used-iphone-checklist/detail-3.webp";

type GuideArticleId = "before-switching" | "data-usage" | "iphone-buy" | "iphone-repair-or-replace" | "used-iphone-checklist";

function Rows({ items }: { items: { title: string; text: string }[] }) {
  return (
    <div className="not-prose my-7 border-y-2 border-foreground-900 bg-white">
      {items.map((item, index) => (
        <div key={item.title} className={`grid gap-2 px-2 py-5 md:grid-cols-[200px_1fr] md:gap-7 md:px-4 ${index ? "border-t border-background-300" : ""}`}>
          <strong className="text-[16px] leading-7 text-foreground-900">{item.title}</strong>
          <div className="text-[15px] leading-7 text-foreground-700">{item.text}</div>
        </div>
      ))}
    </div>
  );
}

function SourceLink({ href, children }: { href: string; children: React.ReactNode }) {
  return <a href={href} target="_blank" rel="noreferrer" className="font-semibold">{children} ↗</a>;
}

function EditorialFigure({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="not-prose my-8 border-y border-background-300 bg-white py-5">
      <img src={src} alt={alt} className="mx-auto w-full max-w-[560px] object-contain" />
      <figcaption className="mt-4 px-3 text-center text-sm leading-6 text-foreground-600">{caption}</figcaption>
    </figure>
  );
}

function ArticleNext({ primary = "/diagnosis" }: { primary?: string }) {
  return (
    <section id="next-actions">
      <h2>次にすること</h2>
      <p>判断材料がそろったら、候補を増やしすぎず、同じ条件で比べます。分からない項目が残っていても、先に整理できるところからで大丈夫です。</p>
      <div>
        <Link to={primary}><small>使い方から候補を整理</small><strong>料金診断へ進む</strong><span>→</span></Link>
        <Link to="/compare"><small>料金とサポートを確認</small><strong>通信会社を比較する</strong><span>→</span></Link>
      </div>
    </section>
  );
}

function BeforeSwitchingArticle() {
  return (
    <>
      <section id="intro">
        <p className="text-[1.08em] leading-[1.9] text-foreground-900">乗り換えで困るのは、申込画面ではなく、その前の確認を飛ばしたときです。安いプランを見つけても、端末代、家族割、メールアドレス、データ移行が後から分かると、予定どおりに進みません。</p>
        <p>先に5つだけ確認すれば、「安くなったけれど不便になった」を避けやすくなります。</p>
      </section>
      <section id="answer">
        <h2>最初に申し込まない。今の契約を1枚にまとめる</h2>
        <p>必要なのは、現在の通信費、データ使用量、電話の使い方、家族・自宅回線との割引、引き継ぐものです。候補探しはその後で十分です。</p>
        <aside><strong>乗り換え日を決める前に、端末代とメールを確認。</strong><p>通信会社を変えても端末の分割払いが残る場合があります。キャリアメールを使い続ける場合は、持ち運びサービスの条件も確認します。</p></aside>
      </section>
      <section id="five-checks">
        <h2>乗り換え前に確認する5つ</h2>
        <Rows items={[
          { title: "1. 今の通信費", text: "請求総額ではなく、基本料金・通話料・オプションを分けます。端末代は別欄に書きます。" },
          { title: "2. データ使用量", text: "契約容量ではなく直近3か月の実使用量を見ます。最も多い月に少し余裕を持たせます。" },
          { title: "3. 電話の使い方", text: "1回の長さと回数を確認します。5分無料、10分定額、完全かけ放題のどれが必要かが分かります。" },
          { title: "4. 割引のつながり", text: "家族割、自宅の光回線、電気、カード特典を確認します。1回線の変更が家族全体へ影響する場合があります。" },
          { title: "5. 引き継ぐもの", text: "電話番号、メール、写真、LINE、認証アプリ、電子マネーを確認。端末も変える場合は、回線切替より前にバックアップします。" },
        ]} />
        <div className="not-prose my-8 grid gap-6 md:grid-cols-2">
          <figure className="border-y border-background-300 bg-white py-4">
            <img src={photoTransferImage} alt="2台のスマートフォン間で写真を移すイラスト" className="w-full object-contain" />
            <figcaption className="mt-3 px-3 text-center text-sm leading-6 text-foreground-600">写真は、移行方法と家族への共有方法を分けて確認します。</figcaption>
          </figure>
          <figure className="border-y border-background-300 bg-white py-4">
            <img src={lineTransferImage} alt="メッセージ履歴を引き継ぐイラスト" className="w-full object-contain" />
            <figcaption className="mt-3 px-3 text-center text-sm leading-6 text-foreground-600">LINEは、アカウントと残したい履歴を別に確認します。</figcaption>
          </figure>
        </div>
        <EditorialFigure
          src={suicaAuthImage}
          alt="改札でスマートフォンを使う場面と認証キーのイラスト"
          caption="Suicaや認証アプリは、データコピーとは別に公式の移行手順を確認します。"
        />
      </section>
      <section id="timing">
        <h2>手続きは「前日・当日・翌日」に分ける</h2>
        <h3>前日まで</h3><p>本人確認書類、支払い方法、ログインIDを用意し、端末の対応状況とSIMの種類を確認します。端末も変えるならバックアップを取ります。</p>
        <h3>回線切替の日</h3><p>連絡が取れる時間に行い、Wi-Fi環境を確保します。開通後は発着信、モバイル通信、SMSを確認します。</p>
        <h3>翌日以降</h3><p>旧回線の会員ページで最終請求、端末残債、返却手続きの有無を確認します。不要になったオプションが別契約で残っていないかも見ます。</p>
      </section>
      <section id="mistakes">
        <h2>よくある失敗は、料金以外で起きる</h2>
        <ul><li>今の端末が新しい回線に対応していなかった</li><li>家族割が外れ、世帯全体では安くならなかった</li><li>キャリアメールを登録したサービスへログインできなくなった</li><li>端末返却の期限や状態条件を見落とした</li><li>店頭サポートがあると思ってオンライン専用プランを選んだ</li></ul>
        <p>手続き方式や受付条件は変わることがあります。申し込み時は乗り換え先の公式ページで確認してください。確認日：2026年9月18日。</p>
      </section>
      <EditorialFigure
        src={switchingChecklistImage}
        alt="2台のスマートフォンと乗り換え前のチェックリスト"
        caption="申し込み前に、端末・回線・引き継ぎ・支払い条件をもう一度確認します。"
      />
      <ArticleNext />
    </>
  );
}

function DataUsageArticle() {
  return (
    <>
      <section id="intro">
        <p className="text-[1.08em] leading-[1.9] text-foreground-900">「20GBの契約だから、20GB必要」とは限りません。毎月余っているなら、選ぶ基準は契約容量ではなく実際に使った量です。</p>
        <p>直近3か月を並べると、たまたま多かった月と、普段必要な量を分けて考えられます。</p>
      </section>
      <section id="answer">
        <h2>必要量は「一番多い月＋少しの余裕」で決める</h2>
        <p>1か月だけで決めると、旅行やOS更新などの影響を受けます。直近3か月の使用量を確認し、最大の月に1〜3GBほど余裕を持たせると選びやすくなります。</p>
      </section>
      <section id="check-usage">
        <h2>まず、スマホではなく通信会社の記録を見る</h2>
        <p>通信会社の会員ページやアプリに表示される「データ利用量」を3か月分メモします。スマホ本体の集計期間と請求期間がずれていることがあるため、料金プランを決めるときは通信会社側の記録を基準にします。</p>
        <div className="not-prose my-7 border-y-2 border-foreground-900 bg-white px-2 py-5 md:px-4">
          <div className="grid grid-cols-3 gap-4 text-center"><div><span className="block text-xs text-foreground-500">3か月前</span><strong className="text-xl">6.2GB</strong></div><div><span className="block text-xs text-foreground-500">2か月前</span><strong className="text-xl">8.1GB</strong></div><div><span className="block text-xs text-foreground-500">先月</span><strong className="text-xl">7.4GB</strong></div></div>
          <p className="mt-4 mb-0 border-t border-background-300 pt-4 text-sm leading-7 text-foreground-700">この例なら、10GB前後のプランから検討。20GBを前提にする必要はありません。</p>
        </div>
        <EditorialFigure src={dataUsageHistoryImage} alt="スマートフォンのデータ使用量を3か月分比べるイラスト" caption="直近数か月を並べ、普段の月と多かった月を分けて見ます。" />
      </section>
      <section id="guide">
        <h2>容量帯ごとの考え方</h2>
        <Rows items={[
          { title: "1〜3GB", text: "自宅・職場ではWi-Fi。外では連絡、地図、決済が中心。動画をほとんど見ない人。" },
          { title: "4〜10GB", text: "SNSやWebを毎日使い、短い動画も見る。Wi-Fi外での利用はほどほど。" },
          { title: "11〜30GB", text: "通勤中の動画・音楽、テザリングを使う日がある。外で過ごす時間が長い。" },
          { title: "30GB超", text: "高画質動画やテザリングを日常的に使う。自宅回線の代わりに使う場合は無制限帯も比較。" },
        ]} />
        <p>これは目安です。動画の画質、アプリの自動更新、写真のバックアップで使用量は変わります。</p>
        <EditorialFigure src={dataUsageCapacityImage} alt="データ容量の違いをメーターで見比べるイラスト" caption="容量の数字だけでなく、外で何に使うかも一緒に確認します。" />
      </section>
      <section id="reduce">
        <h2>容量を下げる前に、勝手に増える通信を確認</h2>
        <ul><li>写真・動画のクラウド同期をモバイル通信で行っていないか</li><li>動画アプリの画質が「高画質」固定になっていないか</li><li>アプリ更新をWi-Fi接続時だけにできるか</li><li>テザリングしたパソコンが更新を始めていないか</li></ul>
        <p>我慢して容量を減らすのではなく、不要な通信を止めた後の使用量で考えます。</p>
        <EditorialFigure src={dataUsageSceneImage} alt="自宅のWi-Fiと外出先でスマートフォンを使い分けるイラスト" caption="自宅のWi-Fi利用と外出先の通信を分けると、必要な容量が見えやすくなります。" />
      </section>
      <ArticleNext />
    </>
  );
}

function IphoneBuyArticle() {
  return (
    <>
      <section id="intro"><p className="text-[1.08em] leading-[1.9] text-foreground-900">同じiPhoneでも、Apple、通信会社、家電量販店、中古店では、買い方と支払いの条件が違います。購入先を比べる前に、約2年で返却するか、返却せず使い続けるかを決めましょう。</p><p>この記事では購入先の特徴を整理します。iPhone 18 Pro 256GBの具体的な端末代は、<Link to="/articles/iphone-18-pro-mnp">購入先別の比較記事</Link>にまとめています。</p></section>
      <section id="answer"><h2>先に結論：返却するか、使い続けるかで分ける</h2><p>返却する場合は、返却までの端末代・返却時の利用料・通信費を別々に確認します。返却しない場合は、本体の支払総額と通信費を確認します。この2つの金額を同じ順位表で比べないことが大切です。</p><EditorialFigure src={iphoneBuyMethodImage} alt="iPhoneの買い切りと返却プログラムを比べるイラスト" caption="返却する場合と使い続ける場合で、確認する費用が変わります。" /></section>
      <section id="where"><h2>購入先ごとの違い</h2><Rows items={[
        { title: "Apple", text: "回線契約と分けて買いやすく、買い切り総額を把握しやすい。通信会社の乗り換え割引はありません。" },
        { title: "通信会社", text: "回線と同時に申し込みやすく、乗り換え割引や返却プログラムがあります。適用条件と返却時の査定を確認します。" },
        { title: "家電量販店", text: "ポイントや相談窓口が利点。店舗独自条件、指定プラン、オプション加入がないか総額で見ます。" },
        { title: "中古販売店", text: "旧モデルを安く買える一方、バッテリー、保証、修理履歴、ネットワーク利用制限の確認が必要です。" },
      ]} /></section>
      <section id="compare"><h2>購入先ごとに、4つの費用を分ける</h2><ol><li>返却までに払う端末代、または返却せず完済する端末代。</li><li>乗り換え（MNP）による本体値引きが使えるか。</li><li>返却する場合の利用料・手数料と返却期限。</li><li>毎月の通信費・必要なオプション料。</li></ol><aside><strong>「実質○円」は、返却や割引の条件も確認。</strong><p>ポイント還元は現金での値引きと分けます。返却せず使い続ける場合の端末代も、別に確認してください。</p></aside><EditorialFigure src={iphoneBuyTotalImage} alt="端末代と保証やポイントを含む総額を整理するイラスト" caption="端末代・返却時の費用・通信費を分けて比べます。" /></section>
      <section id="choice"><h2>こんな選び方なら整理しやすい</h2><Rows items={[
        { title: "長く使いたい", text: "返却せずに完済する端末代、保証、必要になったときのバッテリー交換を確認します。" },
        { title: "2年ほどで替えたい", text: "返却までの端末代、返却時の利用料、返却期限、破損時の追加費用を確認します。" },
        { title: "初期設定が不安", text: "価格差だけでなく、購入店がデータ移行や設定をどこまで支援するか確認。" },
        { title: "旧モデルで十分", text: "OSサポート期間、容量、カメラ、端子の違いを確認して新品旧型・認定中古も候補に。" },
      ]} /><EditorialFigure src={iphoneBuyUsedImage} alt="中古iPhoneの状態を確認するイラスト" caption="旧モデルや中古を選ぶときは、価格と端末の状態をセットで確認します。" /><p>Appleでの購入条件は<SourceLink href="https://www.apple.com/jp/iphone/buy/">iPhone購入ページ</SourceLink>、修理・保証は<SourceLink href="https://support.apple.com/ja-jp/iphone">Appleサポート</SourceLink>で確認できます。確認日：2026年9月18日。</p></section>
      <ArticleNext primary="/articles/used-iphone-checklist" />
    </>
  );
}

function RepairArticle() {
  return (
    <>
      <section id="intro"><p className="text-[1.08em] leading-[1.9] text-foreground-900">画面が割れた、電池がもたない、動作が遅い。そんなとき、修理代だけを見て買い替えを決めると、まだ使える端末を手放したり、反対に修理を重ねて高くついたりします。</p><p>故障箇所、今後使いたい年数、買い替えた場合の総額を順番に比べます。</p></section>
      <section id="answer"><h2>先に結論：修理後に2年使いたいかで考える</h2><p>バッテリーだけが弱り、ほかに不満がなければ修理の効果を感じやすいケースです。複数箇所の故障、OS対応への不安、容量不足が重なるなら、買い替えも並べます。</p></section>
      <section id="three-axes"><h2>判断する3つの軸</h2><Rows items={[
        { title: "故障の範囲", text: "バッテリーだけか、画面・カメラ・充電口・基板まで複数か。まず診断と見積もりを取ります。" },
        { title: "使い続けたい期間", text: "修理後に1〜2年以上使いたいか。ほかの不満が残るなら、修理費だけで決めません。" },
        { title: "買い替え総額", text: "端末価格だけでなく、返却条件、データ移行、ケースや充電器、残っている端末代も含めます。" },
      ]} /><EditorialFigure src={repairBatteryImage} alt="スマートフォンの画面修理とバッテリー交換を検討するイラスト" caption="故障箇所を分けると、修理で解決する問題か判断しやすくなります。" /></section>
      <section id="battery"><h2>電池が気になるなら、最大容量を確認</h2><p>「設定」→「バッテリー」→「バッテリーの状態と充電」で最大容量を確認できます。数字だけでなく、突然電源が落ちる、著しく熱くなる、膨らんでいるなどの症状があれば使用を続けず、正規の窓口へ相談してください。</p><p>AppleCareプランでは、条件を満たすバッテリー交換が追加料金なしになる場合があります。保証状況と見積額を確認します。</p></section>
      <section id="decision"><h2>修理と買い替えを同じ紙に書く</h2><Rows items={[
        { title: "修理を優先", text: "不満が1か所だけ／修理後も十分使える／データ移行を避けたい／保証が使える。" },
        { title: "買い替えも比較", text: "故障が複数／容量不足／OSやアプリ対応が不安／修理後も別の不満が残る。" },
      ]} /><EditorialFigure src={repairCostImage} alt="修理費と買い替え費用を比べるイラスト" caption="修理費だけでなく、今後使える年数と買い替え総額を同じ紙で比べます。" /><aside><strong>修理前にもバックアップ。</strong><p>修理内容によって初期化や本体交換になる可能性があります。写真、LINE、認証アプリ、電子マネーの移行準備をしてから預けます。</p></aside><EditorialFigure src={repairBackupImage} alt="修理前にスマートフォンのデータをバックアップするイラスト" caption="修理へ出す前に、写真・連絡先・認証アプリなどをバックアップします。" /><p>見積額と受付方法は<SourceLink href="https://support.apple.com/ja-jp/iphone/repair">AppleのiPhone修理サービス</SourceLink>、バッテリーは<SourceLink href="https://support.apple.com/ja-jp/iphone/repair/battery-replacement">バッテリーサービス</SourceLink>で確認できます。確認日：2026年9月18日。</p></section>
      <ArticleNext primary="/articles/iphone-buy" />
    </>
  );
}

function UsedIphoneArticle() {
  return (
    <>
      <section id="intro"><p className="text-[1.08em] leading-[1.9] text-foreground-900">中古iPhoneは、世代を少し下げるだけで予算を抑えられます。ただし、同じ機種名でもバッテリー、修理歴、保証で中身は違います。</p><p>安い理由が説明できない端末は避け、購入前に7項目を確認します。</p></section>
      <section id="answer"><h2>先に結論：本体の傷より、使えない原因を先に見る</h2><p>最優先はアクティベーションロック、ネットワーク利用制限、SIM対応です。ここに問題があると、外観がきれいでも使い始められません。</p><aside><strong>「iPhoneは所有者にロックされています」と表示される端末は購入しない。</strong><p>前の所有者のApple Accountに紐づいたままです。販売者による解除が確認できるまで引き取らないでください。</p></aside><EditorialFigure src={usedIphoneLockImage} alt="中古iPhoneのロック状態とSIM対応を確認するイラスト" caption="外観より先に、ロック・利用制限・SIM対応を確認します。" /></section>
      <section id="seven-checks"><h2>購入前の確認7項目</h2><Rows items={[
        { title: "1. ロック状態", text: "初期設定で前所有者のApple Accountを求められないこと。『こんにちは』画面から進められる状態か確認。" },
        { title: "2. 利用制限", text: "IMEIを使い、販売元や元の通信会社でネットワーク利用制限を確認。判定の意味と保証も確認。" },
        { title: "3. SIM対応", text: "『設定』→『一般』→『情報』でSIMロックを確認。使う通信会社の周波数・eSIMにも対応するか見る。" },
        { title: "4. バッテリー", text: "最大容量だけでなく、交換歴や『不明な部品』表示を確認。交換費用を足して新品・整備済品と比較。" },
        { title: "5. 修理履歴", text: "対応機種では『部品と修理の履歴』を確認。純正・中古・不明などの表示と機能への影響を見る。" },
        { title: "6. 基本動作", text: "画面、Face ID／Touch ID、カメラ、スピーカー、マイク、充電口、ボタン、Wi-Fiを試す。" },
        { title: "7. 保証と返品", text: "初期不良の期間、バッテリーが保証対象か、自己都合返品の可否を購入前に文章で確認。" },
      ]} /><EditorialFigure src={usedIphoneConditionImage} alt="中古iPhoneのバッテリーやカメラの状態を確認するイラスト" caption="バッテリー・修理履歴・カメラなど、使い続けるための状態を見ます。" /></section>
      <section id="seller"><h2>個人売買と中古店の価格差は、保証の差でもある</h2><p>個人売買は安い一方、状態確認とトラブル対応を自分で行います。初めてなら、検品内容・赤ロム保証・返品条件を明示する中古店の方が判断しやすくなります。</p><p>届いたら返品期間内に7項目をもう一度確認し、問題があればデータを入れる前に連絡します。</p><EditorialFigure src={usedIphoneTestImage} alt="中古iPhoneの画面やカメラなどを動作確認するイラスト" caption="返品期間内に、画面・カメラ・音・充電などの基本動作を試します。" /></section>
      <section id="source"><h2>Apple公式の確認手順も使う</h2><p><SourceLink href="https://support.apple.com/ja-jp/104999">Apple「中古のiPhoneを購入する場合」</SourceLink>には、損傷、バッテリー、SIMロック、カメラなどの確認手順があります。<SourceLink href="https://support.apple.com/ja-jp/102658">部品と修理サービスの履歴</SourceLink>も合わせて確認してください。確認日：2026年9月18日。</p></section>
      <ArticleNext primary="/articles/iphone-buy" />
    </>
  );
}

export default function GuideEditorialArticle({ id }: { id: GuideArticleId }) {
  if (id === "before-switching") return <BeforeSwitchingArticle />;
  if (id === "data-usage") return <DataUsageArticle />;
  if (id === "iphone-buy") return <IphoneBuyArticle />;
  if (id === "iphone-repair-or-replace") return <RepairArticle />;
  return <UsedIphoneArticle />;
}

